import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Grid,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  FilterList,
  Clear,
  Add as AddIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../auth";
import ListLayout from "../../../../components/Layout/ListLayout";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import {
  getEnableStockingAreaMutationOptions,
  getDisableStockingAreaMutationOptions,
  getDeleteStockingAreaMutationOptions,
  getListStockingAreaQueryOptions,
} from "../../../../api/bpm/endpoints";
import type { StockingAreaDto } from "../../../../api/bpm/model";
import { FormTextField } from "../../../../forms/FormTextField";
import { LocationCards } from "./components/LocationCards";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { LogsModal } from "../../../../components/LogsModal";
import { useTranslation } from "react-i18next";

const locationsSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
  enabled: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/__auth/admin/locations/")({
  component: AdminLocationsPage,
  validateSearch: (search) => locationsSearchSchema.parse(search),
  staticData: {
    title: "Áreas de lotação",
  },
});

function LocationActionsCell({ row }: { row: StockingAreaDto }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tenant = user?.tenant || "default-domain";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const enableMutation = useMutation({
    ...getEnableStockingAreaMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["listStockingarea"],
      });
      setIsConfirmOpen(false);
    },
  });

  const disableMutation = useMutation({
    ...getDisableStockingAreaMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["listStockingarea"],
      });
      setIsConfirmOpen(false);
    },
  });

  const deleteMutation = useMutation({
    ...getDeleteStockingAreaMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["listStockingarea"],
      });
      setIsDeleteConfirmOpen(false);
    },
  });

  const handleToggleStatus = () => {
    if (!row.id) return;
    if (row.enabled) {
      disableMutation.mutate({
        tenant,
        stockingAreaId: Number(row.id),
      });
    } else {
      enableMutation.mutate({
        tenant,
        stockingAreaId: Number(row.id),
      });
    }
  };

  const handleDelete = () => {
    if (!row.id) return;
    deleteMutation.mutate({
      tenant,
      stockingAreaId: Number(row.id),
    });
  };

  const isStatusLoading = enableMutation.isPending || disableMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            navigate({ to: `/admin/locations/${row.id}` });
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.view")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            navigate({ to: `/admin/locations/${row.id}/edit` });
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.edit")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            {row.enabled ? (
              <BlockIcon fontSize="small" color="error" />
            ) : (
              <CheckCircleIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>{row.enabled ? t("common.deactivate") : t("common.activate")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsLogsModalOpen(true);
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsDeleteConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.delete")}</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleToggleStatus}
        title={
          row.enabled ? t("admin.locations.deactivateTitle") : t("admin.locations.activateTitle")
        }
        description={
          row.enabled
            ? t("admin.locations.deactivateConfirmation", { name: row.name })
            : t("admin.locations.activateConfirmation", { name: row.name })
        }
        confirmText={row.enabled ? t("common.deactivate") : t("common.activate")}
        confirmColor={row.enabled ? "error" : "primary"}
        isLoading={isStatusLoading}
      />

      <ConfirmModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t("admin.locations.deleteTitle")}
        description={t("admin.locations.deleteConfirmation", { name: row.name })}
        confirmText={t("common.delete")}
        confirmColor="error"
        isLoading={isDeleteLoading}
      />

      <LogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        entityId={row.id?.toString() || ""}
        entityName={row.name || ""}
        tenant={tenant}
        entityType="stocking-areas"
      />
    </>
  );
}

const filterSchema = z.object({
  q: z.string().optional(),
  enabled: z.string().optional(),
});

type LocationFiltersData = z.infer<typeof filterSchema>;

interface LocationFiltersProps {
  onFilter: (data: LocationFiltersData) => void;
  onClear: () => void;
  isLoading?: boolean;
}

function LocationFilters({
  onFilter,
  onClear,
  isLoading,
}: LocationFiltersProps) {
  const { t } = useTranslation();
  const methods = useForm<LocationFiltersData>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      q: "",
      enabled: "all",
    },
  });

  const { handleSubmit, reset } = methods;

  const handleClear = () => {
    reset({ q: "", enabled: "all" });
    onClear();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFilter)}>
        <Grid
          container
          spacing={2}
          sx={{ mb: 3, p: 2, bgcolor: "background.paper", borderRadius: 1 }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <FormTextField
              name="q"
              label={t("admin.common.nameOrDescription")}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <FormTextField
              name="enabled"
              label={t("admin.common.situation")}
              size="small"
              fullWidth
              select
            >
              <MenuItem value="all">{t("common.all")}</MenuItem>
              <MenuItem value="true">{t("common.active")}</MenuItem>
              <MenuItem value="false">{t("common.inactive")}</MenuItem>
            </FormTextField>
          </Grid>

          <Grid size={12}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                onClick={handleClear}
                startIcon={<Clear />}
              >{t("common.clear")}</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                startIcon={<FilterList />}
              >{t("common.filter")}</Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}

function AdminLocationsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const tenant = user?.tenant || "default-domain";

  const { data: resultData, isLoading, error } = useQuery({
    ...getListStockingAreaQueryOptions(tenant, {
            page: search.page - 1,
            size: search.size,
            q: search.q,
            enabled: search.enabled === "true"
                            ? true
                            : search.enabled === "false"
                              ? false
                              : undefined
        } as any),
  });
  const errorAny = error as any;

  const data = resultData as any;

  const handleFilter = (filters: LocationFiltersData) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...filters,
        enabled:
          filters.enabled === "all"
            ? undefined
            : (filters.enabled as "true" | "false" | undefined),
        page: 1,
      }),
    });
  };

  const handleClearFilters = () => {
    navigate({
      search: (prev) => ({
        page: 1,
        size: prev.size,
      }),
    });
  };

  const columnHelper = createColumnHelper<StockingAreaDto>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        header: t("admin.common.uniqueId"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("name", {
        header: t("common.name"),
        cell: (info) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {info.getValue()}
            </Typography>
          </Box>
        ),
      }),
      columnHelper.accessor("description", {
        header: t("admin.common.description"),
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("creator", {
        header: t("admin.common.createdBy"),
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("enabled", {
        header: t("admin.common.situation"),
        cell: (info) => (
          <Chip
            label={info.getValue() ? t("common.active") : t("common.inactive")}
            color={info.getValue() ? "success" : "default"}
            size="small"
            variant="outlined"
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: t("common.actions"),
        cell: (info) => <LocationActionsCell row={info.row.original} />,
        meta: { align: "right", isSticky: true },
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    data: data?.content || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: data?.totalPages || -1,
  });

  return (
    <ListLayout
      title="Áreas de lotação"
      subtitle={t("admin.locations.subtitle")}
      isLoading={isLoading}
      error={errorAny}
      isEmpty={!data?.content?.length}
      filters={
        <LocationFilters
          onFilter={handleFilter}
          onClear={handleClearFilters}
          isLoading={isLoading}
        />
      }
      actions={
        <IconButton
          onClick={() => navigate({ to: "/admin/locations/create" })}
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
            boxShadow: 2,
          }}
        >
          <AddIcon />
        </IconButton>
      }
    >
      {isMobile ? (
        <LocationCards locations={data?.content || []} />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "background.default" }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta: any = header.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={header.id}
                        sx={{
                          fontWeight: 600,
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "background.default",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta: any = cell.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={cell.id}
                        sx={{
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "inherit",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ListLayout>
  );
}
